import type {
    AdIntelligenceRecordsEnhancedAdsEntitySingleModeRecord,
    AdIntelligenceRecordsEnhancedAdsEntityCompareModeRecord,
} from "@similarweb/pro-api-client";

import { useApiQuery } from "app/modules/api-client";
import type { Data } from "app/modules/api-client/queryTypes";
import { useDisplayAdvertiserPublisherDataAndParams } from "app/pages/digital-marketing/ad-intelligence/common/hooks/useDisplayAdvertiserPublisherDataAndParams";
import type { ISortProps } from "app/pages/digital-marketing/ad-intelligence/common/utils/utils";
import { getCategoryParam } from "app/pages/digital-marketing/ad-intelligence/common/utils/utils";
import DurationService, { BasicDurations } from "app/services/DurationService";

import type { AdIntelligenceController } from "../../shared/types/types";

const initialTableSort: ISortProps = { asc: false, sort: "impressions" };

type RecordItem =
    | AdIntelligenceRecordsEnhancedAdsEntitySingleModeRecord
    | AdIntelligenceRecordsEnhancedAdsEntityCompareModeRecord;

type MethodName =
    | "adIntelligenceAdvertiserPublishersBreakdownCompareModeList"
    | "adIntelligenceAdvertiserPublishersBreakdownList"
    | "adIntelligencePublisherAdvertisersCompareModeList"
    | "adIntelligencePublisherAdvertisersBreakdownList";

const getMethodName = (controller: AdIntelligenceController, isCompareMode: boolean): MethodName => {
    if (controller === "advertiser") {
        return isCompareMode
            ? ("adIntelligenceAdvertiserPublishersBreakdownCompareModeList" as const)
            : ("adIntelligenceAdvertiserPublishersBreakdownList" as const);
    }

    return isCompareMode
        ? ("adIntelligencePublisherAdvertisersCompareModeList" as const)
        : ("adIntelligencePublisherAdvertisersBreakdownList" as const);
};

const transformItem = (item: RecordItem, rowIndex: number, resource: AdIntelligenceController, isChild = false) => {
    const entityInfo = {
        url: item.entity,
        favicon: item.favicon,
    };

    return {
        key: `${item.entity}_${rowIndex}_${isChild}`,
        isChild,
        ...(resource === "advertiser" ? { advertiser: entityInfo } : { publisher: entityInfo }),
        impressions: {
            total: item.impressions,
            value: item.impressionsShare,
        },
        impressionsChange: {
            value: item.impressionsChange,
            isNew: item.isImpressionNew,
        },
        spend: { total: item.spend, value: item.spendShare },
        spendChange: { value: item.spendChange, isNew: item.isSpendNew },
        category: {
            id: item.category,
            name: item.category?.replace(/_/g, " ").replace("~", " > "),
        },
        rank: item.rank,
        visits: { total: undefined, value: item.visitsShare },
        visitsChange: { value: item.visitsChange, isNew: item.isVisitsNew },
    };
};

type ApiResponse = Data<"AdIntelligenceMediaPartners", MethodName>;

const selectTransformedData = (response: ApiResponse, resource: AdIntelligenceController, withChildren: boolean) => {
    if (!response?.records) {
        return;
    }
    const records = response.records as RecordItem[];
    return {
        totalCount: response.totalCount,
        hasAdIntelMetrics: response.hasAdIntelMetrics,
        items: records.map((item, rowIndex) => ({
            ...transformItem(item, rowIndex, resource, false),
            ...(withChildren
                ? {
                      children: item.children?.map((i) => transformItem(i as RecordItem, rowIndex, resource, true)),
                  }
                : {}),
        })),
    };
};

type ITableData = ReturnType<typeof selectTransformedData>;

interface IUseTableRowsParams {
    controller: AdIntelligenceController;
    resource: AdIntelligenceController;
    page: number;
    pageSize: number;
    isCompareMode: boolean;
    search?: string;
    sort?: ISortProps;
    withChildren?: boolean;
    enabled?: boolean;
    gapParams?: { gapDomains: string[]; gapMetric: string };
}

export const useTableRows = ({
    controller,
    resource,
    page,
    pageSize,
    isCompareMode,
    search,
    sort = initialTableSort,
    withChildren = true,
    enabled = true,
    gapParams,
}: IUseTableRowsParams) => {
    const params = useDisplayAdvertiserPublisherDataAndParams();
    const { from, to, isWindow } = DurationService.getDurationData(params.duration).forAPI;

    const methodName = getMethodName(controller, isCompareMode);

    const { data, isLoading, isFetching, isError } = useApiQuery<
        "AdIntelligenceMediaPartners",
        typeof methodName,
        ITableData
    >(
        {
            tag: "AdIntelligenceMediaPartners",
            methodName: methodName,
            options: {
                enabled,
                select: (response) => selectTransformedData(response, resource, withChildren),
            },
        },
        {
            country: params.country,
            key: params.key.split(","),
            from,
            to,
            page,
            pageSize,
            includeTerms: search,
            isWindow,
            network: params.network,
            ...getCategoryParam(params.category),
            ...(isWindow ? { latest: BasicDurations.LAST_TWENTY_EIGHT_DAYS } : {}),
            ...sort,
            ...(gapParams ? { gapDomain: gapParams.gapDomains, gapMetric: gapParams.gapMetric } : {}),
        },
    );

    return {
        data: !isError && data ? data : undefined,
        isLoading: isLoading || isFetching,
        isError,
    };
};
